import { useRef, type MouseEvent } from 'react';
import type { ApplicationState, SpatialWindow } from '@/types';
import { useEngine } from '@/app/EngineContext';
import { useWorkspaceStore, selectActiveDocument } from '@/store/workspaceStore';
import { useWindowStore } from '@/store/windowStore';
import { setApplicationStateCommand } from '@/features/objects/objectCommands';
import { closeWindow, focusWindow, minimizeWindow, toggleMaximize } from '@/features/windows/windowManager';
import { useApplicationApi } from '@/features/windows/useApplicationApi';
import { stopCanvasPointer, useIsolateWheel } from '@/hooks/useStopCanvasEvents';
import type { ObjectRendererProps } from './types';
import { IconButton } from '@/components/ui';
import MinimizeIcon from '@mui/icons-material/Minimize';
import CropSquareIcon from '@mui/icons-material/CropSquare';
import FilterNoneIcon from '@mui/icons-material/FilterNone';
import CloseIcon from '@mui/icons-material/Close';
import styles from '../ObjectView.module.css';

/**
 * Window chrome: title bar with controls, and the application body. The chrome owns
 * focus, minimize/maximize/close and event isolation; the application receives props
 * and an API and never touches stores. Minimized windows are not mounted at all
 * (the ObjectLayer culls them), so their state simply persists in the document.
 */
export const WindowRenderer = ({ object: window }: ObjectRendererProps<SpatialWindow>) => {
  const { applications, scene } = useEngine();
  const dispatch = useWorkspaceStore((s) => s.dispatch);
  const active = useWindowStore((s) => s.activeWindowId === window.id);
  const manifest = applications.get(window.applicationId);
  const observes = manifest?.capabilities.observesWorkspace ?? false;
  // Workspace-observing apps re-render on document/selection changes; others never do.
  useWorkspaceStore((s) => (observes ? s.revision : 0));
  useWindowStore((s) => (observes ? s.focusHistory.length : 0));
  const api = useApplicationApi(scene, applications);
  const bodyRef = useRef<HTMLDivElement>(null);
  useIsolateWheel(bodyRef);

  const setState = (next: ApplicationState) => {
    const doc = selectActiveDocument(useWorkspaceStore.getState());
    if (!doc) return;
    const command = setApplicationStateCommand(doc, window.id, next);
    if (command) dispatch(command);
  };

  const control = (fn: () => void) => (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    fn();
  };
  const caps = manifest?.capabilities;

  return (
    <div
      className={styles.window}
      data-active={active || undefined}
      data-window-state={window.windowState}
      onPointerDownCapture={() => {
        if (!active) focusWindow(window.id);
      }}
    >
      <header
        className={styles.titlebar}
        onDoubleClick={() => {
          if (caps?.maximizable !== false) toggleMaximize(window.id);
        }}
      >
        <span className={styles.windowIcon} aria-hidden="true">
          {manifest?.icon ?? '▢'}
        </span>
        <strong>{window.name}</strong>
        <span>{manifest?.name ?? window.applicationId}</span>
        <span className={styles.windowControls} onPointerDown={stopCanvasPointer}>
          {caps?.minimizable !== false && (
            <IconButton
              className={styles.windowButton}
              label="Minimize"
              shortcut="⌘M"
              data-testid="window-minimize"
              onClick={control(() => {
                minimizeWindow(window.id);
              })}
            >
              <MinimizeIcon sx={{ fontSize: 14 }} />
            </IconButton>
          )}
          {caps?.maximizable !== false && (
            <IconButton
              className={styles.windowButton}
              label={window.windowState === 'maximized' ? 'Restore' : 'Maximize'}
              shortcut="⌥⌘M"
              data-testid="window-maximize"
              onClick={control(() => {
                toggleMaximize(window.id);
              })}
            >
              {window.windowState === 'maximized' ? (
                <FilterNoneIcon sx={{ fontSize: 12 }} />
              ) : (
                <CropSquareIcon sx={{ fontSize: 14 }} />
              )}
            </IconButton>
          )}
          <IconButton
            className={styles.windowButton}
            label="Close"
            shortcut="⌘W"
            data-testid="window-close"
            onClick={control(() => {
              closeWindow(window.id);
            })}
          >
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </span>
      </header>
      <div ref={bodyRef} className={styles.body} onPointerDown={stopCanvasPointer} data-testid="window-body">
        {manifest ? (
          <manifest.component
            window={window}
            state={manifest.normalizeState(window.applicationState)}
            setState={setState}
            api={api}
            active={active}
          />
        ) : (
          <p className={styles.missingApp}>Application “{window.applicationId}” is not installed.</p>
        )}
      </div>
    </div>
  );
};
